import { useDebounceFn, useMemoizedFn, useMount } from 'ahooks';
import { Form } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxNoProofTip } from 'components/RevxNoProofTip';
import { RevxSpin } from 'components/RevxSpin';
import { CartLineItem } from 'domains/cartDomain';
import {
  AdjustMarkupPayload,
  JobConfigViewer,
  JobEstimator,
  JobEstimatorComparatorItem,
} from 'domains/jobconfigDomain';
import { cloneDeep, isNumber, isUndefined } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QUANTITY_DEBOUNCE_MILLISECOND } from 'utils/constants';
import { StepButtons } from '../ConfigStep/ActionButtons';
import './SupplierComparison.less';
import './ViewQuote.less';
import { NoOtherSupplier } from './comparison/NoOtherSupplier';
import { SourceComparisonItem } from './comparison/SourceComparisonItem';
import { SourceComparisonLabel } from './comparison/SourceComparisonLabel';

interface Props {
  isExpert?: boolean;
  jobConfigViewer?: JobConfigViewer;
  addToCArtHandler?: (e: CartLineItem) => void;
  isProject?: boolean;
}

export const SourceComparison = (props: Props) => {
  const { isExpert, jobConfigViewer, addToCArtHandler } = props;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const configItem = useAppSelector((state) => state.jobConfig.configItem);
  const partSpec = configItem?.specItems.find((i) => i.selected)?.spec;

  // const orgId = useAppSelector((state) => state.app.orgId);
  const [jobEstimator, setJobEstimator] = useState<JobEstimator>();
  const [choiceSerialNumber, setChoiceSerialNumber] = useState<number>(
    configItem?.specItems[0].serialNumber || 0
  );
  const [jobEstimatorLoading, setJobEstimatorLoading] =
    useState<boolean>(false);
  // const [supplierList, setSupplierList] = useState<OrgMembers[]>([]);
  const [showList, setShowList] = useState<string[]>([
    'Services',
    'Shipping Total',
  ]);
  const [form] = useForm();
  const [boxHeight, setBoxHeight] = useState<number>(40);
  const [labelMaxWidth, setLabelMaxWidth] = useState<number>(0);
  const [loadSerialNumber, setLoadSerialNumber] = useState<number[]>([]);

  const cacheChoiceSerialNumber = useAppSelector(
    (state) => state.jobEstimator.choiceSerialNumber
  );
  useMount(async () => {
    if (!jobConfigViewer) {
      defaultComparison();
    }
  });

  const maxLabel = useMemo(() => {
    return 240;
  }, []);

  const defaultComparison = useMemoizedFn(
    async (SerialNumber: number = choiceSerialNumber) => {
      setJobEstimatorLoading(true);
      try {
        console.log(jobConfigViewer);
        let res = jobEstimator;
        // if (
        //   !jobEstimator?.quantityItems.find(
        //     (item) => item.serialNumber === SerialNumber
        //   )
        // ) {
        res = await dispatch.jobEstimator.generate({
          jobConfigId: configItem?.id || jobConfigViewer?.id || '',
          quantityItemSerialNumber: SerialNumber,
        });
        setLoadSerialNumber([...loadSerialNumber, SerialNumber]);
        res?.quantityItems.map((item) => {
          if (
            !item.averageEligible &&
            Object.keys(item.comparatorItems[0]).length
          ) {
            item.comparatorItems = [
              {
                ...item.comparatorItems[0],
                averageEligible: false,
                serialNumber: -1,
              } as JobEstimatorComparatorItem,
              ...item.comparatorItems.map((item) => {
                item.averageEligible = true;
                return item;
              }),
            ];
          } else {
            item.comparatorItems = item.comparatorItems.map((item) => {
              item.averageEligible = true;
              return item;
            });
          }
          return item;
        });
        // res = await dispatch.jobEstimator.getJobEstimator({
        //   jobConfigId: configItem.id,
        // });
        setJobEstimator(res);
        if (isUndefined(choiceSerialNumber)) {
          setChoiceSerialNumber(configItem?.specItems[0].serialNumber || 0);
        }

        setTimeout(() => {
          let maxHeight = 40;
          for (let i in document.getElementsByClassName('jobSvcRef')) {
            const item = document.getElementsByClassName('jobSvcRef')[i];
            if (item?.scrollHeight > maxHeight) {
              maxHeight = item?.scrollHeight;
            }
          }
          setBoxHeight(maxHeight);
          setLabelMaxWidth(
            document.getElementsByClassName('comparison-drawer-label')?.[1]
              ?.scrollWidth
          );
        });
      } catch {
      } finally {
        setJobEstimatorLoading(false);
      }
    }
  );

  useEffect(() => {
    setChoiceSerialNumber(cacheChoiceSerialNumber);
  }, [cacheChoiceSerialNumber]);

  useEffect(() => {
    if (jobConfigViewer) {
      defaultComparison();
    }
  }, [jobConfigViewer, defaultComparison]);

  const updateComparison = async (
    jobEstimatorComparatorItem: JobEstimatorComparatorItem
  ) => {
    setJobEstimatorLoading(true);
    try {
      const jobEstimatorClone = cloneDeep(jobEstimator);
      const idx = jobEstimatorClone?.quantityItems[0].comparatorItems
        .map((item) => item.serialNumber)
        .indexOf(jobEstimatorComparatorItem.serialNumber);
      if (isNumber(idx)) {
        jobEstimatorClone?.quantityItems[0].comparatorItems.splice(idx, 1, {
          ...jobEstimatorComparatorItem,
          averageEligible: true,
        });
        setJobEstimator(jobEstimatorClone);
      }
    } catch {
    } finally {
      setJobEstimatorLoading(false);
    }
  };

  const defaultForm = useMemoizedFn((list: JobEstimatorComparatorItem[]) => {
    let defaultFormData: { [x: string]: any } = {};
    list.forEach((item) => {
      if (!defaultFormData[item.serialNumber + '']) {
        defaultFormData[item.serialNumber + ''] = {};
      }
      item?.comparator?.parts.forEach((part) => {
        if (!defaultFormData[item.serialNumber + ''][part.skuId + '']) {
          defaultFormData[item.serialNumber + ''][part.skuId + ''] = {};
        }
        defaultFormData[item.serialNumber + ''][part.skuId + '']['curSelect'] =
          part.revxPressId;
      });
    });
    form.setFieldsValue(defaultFormData);
  });

  const globalMarkupPrice = useMemoizedFn(
    (price: number, globalMarkupPct?: number) => {
      return +(price * (1 + +(globalMarkupPct || 0) / 100)).toFixed(2);
    }
  );

  const showJobEstimator = useMemo(() => {
    const list = cloneDeep(
      jobEstimator?.quantityItems.find(
        (item) => item.serialNumber === choiceSerialNumber
      )?.comparatorItems || []
    );
    setShowList((s) => [
      ...s,
      ...showJobEstimator.partLabel?.map((part) => part.skuName),
    ]);
    const partLabelSvc: any[] = [];
    list.forEach((item) => {
      for (let i = 0; i < item.comparator?.parts?.length; i++) {
        const curLabelSvc = partLabelSvc.find(
          (partSvcItem) => partSvcItem.skuId === item.comparator.parts[i].skuId
        );
        if (!curLabelSvc) {
          partLabelSvc.push({
            skuId: item.comparator.parts[i].skuId,
            svcs: item.comparator.parts[i].svcs
              .filter((sItem) => sItem.charge)
              .map((sItem) => sItem.name),
          });
        } else {
          curLabelSvc.svcs = Array.from(
            new Set([
              ...curLabelSvc.svcs,
              ...item.comparator.parts[i].svcs
                .filter((sItem) => sItem.charge)
                .map((sItem) => sItem.name),
            ])
          );
        }
      }
    });

    list
      .filter((item) => item.serialNumber > -1)
      .forEach((item) => {
        let priceTotal = 0;
        for (let i = 0; i < item?.comparator?.parts.length; i++) {
          const curPartSvc = item.comparator.parts[i].svcs;
          const curLabelSvc = partLabelSvc.find(
            (partSvcItem) =>
              partSvcItem.skuId === item.comparator.parts[i].skuId
          );
          curLabelSvc.svcs.forEach((defaultItem: string) => {
            if (!curPartSvc.find((partSvc) => partSvc.name === defaultItem)) {
              curPartSvc.push({
                name: defaultItem,
                charge: 0,
              });
            }
          });
          for (let i = 0; i < curPartSvc.length; i++) {
            if (
              !curLabelSvc.svcs.find(
                (defaultItem: string) => defaultItem === curPartSvc[i].name
              )
            ) {
              curPartSvc.splice(i, 1);
            }
          }
          item.comparator.parts[i].paperCharge = globalMarkupPrice(
            item.comparator.parts[i].paperCharge,
            item.comparator.supplierMarkupPct
          );
          item.comparator.parts[i].pressCharge = globalMarkupPrice(
            item.comparator.parts[i].pressCharge,
            item.comparator.supplierMarkupPct
          );
          priceTotal += item.comparator.parts[i].paperCharge;
          priceTotal += item.comparator.parts[i].pressCharge;
          for (let j = 0; j < item.comparator.parts[i].svcs.length; j++) {
            item.comparator.parts[i].svcs[j].charge = globalMarkupPrice(
              item.comparator.parts[i].svcs[j].charge,
              item.comparator.supplierMarkupPct
            );
            priceTotal += item.comparator.parts[i].svcs[j].charge;
          }
        }

        for (let j = 0; j < item?.comparator?.svcs.length; j++) {
          item.comparator.svcs[j].charge = globalMarkupPrice(
            item.comparator.svcs[j].charge,
            item.comparator.supplierMarkupPct
          );
          priceTotal += item.comparator.svcs[j].charge;
        }
        if (item?.comparator?.parcel) {
          if (!item?.boxChargeEditable) {
            item.comparator.parcel.charge = globalMarkupPrice(
              item.comparator?.parcel.charge,
              item.comparator.supplierMarkupPct
            );
          }
          priceTotal += item.comparator?.parcel.charge;
        }
        if (item.comparator.parcel.oldPaperCharge) {
          item.comparator.parts[0].paperCharge =
            item.comparator.parcel.oldPaperCharge;
          return;
        }
        if (
          priceTotal !== item?.comparator?.revxPrintingCharge &&
          item?.comparator?.revxPrintingCharge
        ) {
          item.comparator.parts[0].paperCharge =
            item.comparator.parts[0].paperCharge +
            (item.comparator.revxPrintingCharge -
              priceTotal -
              item.comparator.revxMarkup);
        }
      });
    defaultForm(list);
    return {
      list,
      partLabel: list?.[0]?.comparator?.parts || [],
      svcLabel: list?.[0]?.comparator?.svcs || [],
      rush: jobEstimator?.quantityItems.find(
        (item) => item.serialNumber === choiceSerialNumber
      )?.rush,
    };
  }, [choiceSerialNumber, jobEstimator, defaultForm, globalMarkupPrice]);

  const clickToggle = useMemoizedFn((x: string) => {
    if (showList.find((item) => item === x)) {
      setShowList(showList.filter((item) => item !== x));
    } else {
      setShowList([...showList, x]);
    }
  });

  const { run: updateMarkup, cancel: cancelUpdateMarkup } = useDebounceFn(
    async (payload: AdjustMarkupPayload) => {
      setJobEstimatorLoading(true);
      try {
        if (Object.keys(payload).includes('parcelCost')) {
          const res = await dispatch.jobEstimator.adjustParcel(payload);
          updateComparison({
            ...res,
            comparator: {
              ...res.comparator,
              parcel: {
                ...res.comparator.parcel,
                oldPaperCharge: payload.paperCharge,
              },
            },
          });
        } else {
          const res = await dispatch.jobEstimator.adjustMarkup(payload);
          updateComparison(res);
        }
      } catch {
      } finally {
        setJobEstimatorLoading(false);
      }
    },
    { wait: QUANTITY_DEBOUNCE_MILLISECOND }
  );

  const isClickToggle = useMemoizedFn((x: string) => {
    return Boolean(showList.find((item) => item === x));
  });

  const containerMaxWidth = useMemo(() => {
    return {
      maxWidth: `${6 * maxLabel + 4}px`,
    };
  }, [maxLabel]);

  return (
    <>
      <RevxSpin spinning={jobEstimatorLoading} className="comparison">
        {!Boolean(jobConfigViewer) && (
          <div className="step-heading">
            {t('jobConfig.stepComparison.supplierAnalysis')}
          </div>
        )}
        <RevxFlexLayout
          justify="space-between"
          align="bottom"
          className="view-quote-drawer comparison-drawer"
          style={containerMaxWidth}
        >
          <RevxFlexLayout
            className="comparison-drawer-header-title"
            align="center"
            style={{
              position: 'relative',
              top: '0px',
            }}
          >
            <h5
              style={{
                marginRight: '20px',
              }}
            >
              Order Sourcing
            </h5>

            <RevxNoProofTip
              show={!jobEstimator?.digitalProofRequired && !jobEstimatorLoading}
            />
          </RevxFlexLayout>
          <RevxFlexLayout
            className="comparison-drawer-header"
            hidden={!jobEstimator}
          >
            {Boolean(jobConfigViewer) && (
              <label className="comparison-drawer-header-active">
                <span>{jobConfigViewer?.quantity}</span>
              </label>
            )}
            {configItem?.specItems.map((item) => (
              <label
                key={item.serialNumber}
                className={
                  choiceSerialNumber === item.serialNumber
                    ? `comparison-drawer-header-active`
                    : ''
                }
                onClick={() => {
                  setChoiceSerialNumber(item.serialNumber);
                  dispatch.jobEstimator.setState({
                    choiceSerialNumber: item.serialNumber,
                  });
                  defaultComparison(item.serialNumber);
                }}
              >
                <span>{item.quantity}</span>
              </label>
            ))}
          </RevxFlexLayout>
        </RevxFlexLayout>
        <Form form={form} hidden={!jobEstimator} style={containerMaxWidth}>
          <RevxFlexLayout className="comparison-drawer-body" align="top">
            <SourceComparisonLabel
              maxLabel={maxLabel}
              showJobEstimator={showJobEstimator}
              clickToggle={clickToggle}
              isClickToggle={isClickToggle}
              boxHeight={boxHeight}
              jobEstimator={jobEstimator}
              canAddToCart={
                configItem?.canAddToCart || Boolean(jobConfigViewer)
              }
              spec={partSpec}
            />
            <SourceComparisonItem
              showJobEstimator={showJobEstimator}
              maxLabel={maxLabel}
              clickToggle={clickToggle}
              isClickToggle={isClickToggle}
              labelMaxWidth={labelMaxWidth}
              boxHeight={boxHeight}
              jobEstimator={jobEstimator}
              choiceSerialNumber={choiceSerialNumber}
              updateComparison={updateComparison}
              setJobEstimatorLoading={setJobEstimatorLoading}
              canAddToCart={
                configItem?.canAddToCart || Boolean(jobConfigViewer)
              }
              cancelUpdateMarkup={cancelUpdateMarkup}
              updateMarkup={updateMarkup}
              addToCArtHandler={addToCArtHandler}
              spec={partSpec}
              isExpert={isExpert}
            />

            <NoOtherSupplier listLength={showJobEstimator.list.length} />
          </RevxFlexLayout>
        </Form>
      </RevxSpin>
      <div
        className="step-actions"
        hidden={!jobEstimator || isExpert || !!jobConfigViewer}
      >
        <StepButtons />
      </div>
    </>
  );
};
